import { useMemo, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { createKExecution } from './engine/ku'
import { createTMState } from './engine/turing'
import { ComparisonFigure } from './components/ComparisonFigure'
import { GraphRenderer } from './components/GraphRenderer'
import { HeroMark } from './components/HeroMark'
import {
  compiledTreeLookupKU,
  layoutTreeLookup,
  treeLookupInput,
} from './programs/tree-build-ku'
import { treeLookupTM, treeLookupTMInput } from './programs/tree-build-tm'
import {
  TREE_ADDRESS_PRESETS,
  directionsForAddress,
  displayAddress,
  expectedLeafForAddress,
  type TreeAddressPreset,
} from './programs/tree-lookup-answers'

export default function KolmogorovMachinePage() {
  const [treeAddress, setTreeAddress] = useState<TreeAddressPreset>('RL')
  const [showComparison, setShowComparison] = useState(false)
  const reducedMotion = useReducedMotion() ?? false
  const directions = useMemo(() => directionsForAddress(treeAddress), [treeAddress])
  const kuInitial = useMemo(
    () => createKExecution(treeLookupInput(directions), compiledTreeLookupKU),
    [directions],
  )
  const tmInitial = useMemo(
    () => createTMState(treeLookupTM, treeLookupTMInput(directions)),
    [directions],
  )

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to the explorable</a>
      <a
        className="site-back"
        href="https://www.bengaskin.com/projects"
        aria-label="Back to Ben Gaskin’s projects"
      >
        <span aria-hidden="true">←</span>
        <span>projects</span>
      </a>

      <main id="main-content">
        <header className="km-hero page-rail">
          <div className="km-hero-meta">
            <p className="eyebrow">A formal system · 1958</p>
          </div>
          <h1>A Kolmogorov–Uspensky Machine</h1>
          <div className="km-hero-aside">
            <HeroMark />
            <p className="km-hero-lede">
              What if a computer could work directly on a structured object—replacing one exact
              neighbourhood at a time—instead of scanning a tape?
            </p>
          </div>
        </header>

        <section className="km-section page-rail" aria-labelledby="structure-heading">
          <div className="section-heading">
            <p className="eyebrow">The departure</p>
            <h2 id="structure-heading">Computation already has a shape</h2>
          </div>
          <div className="section-copy">
            <p className="standfirst">
              A Turing machine serialises its working state along a line. Kolmogorov and Uspensky
              instead begin with a finite typed complex—here, a graph—and make bounded local
              structure primitive.
            </p>
            <p>
              Both models are universal. The difference is not what can ultimately be computed,
              but what one step can reach without first recovering structure from an encoding.
            </p>
            <div className="km-thesis" role="note">
              <span>Question</span>
              <p>What becomes visible when the machine operates on relations rather than positions?</p>
            </div>
          </div>
        </section>

        <section className="km-section page-rail" aria-labelledby="state-heading">
          <div className="section-heading">
            <p className="eyebrow">The state</p>
            <h2 id="state-heading">One graph, three exact regions</h2>
          </div>
          <div className="section-copy">
            <p className="standfirst">
              From the unique working vertex <code>T0</code>, a fixed radius partitions the state
              into the part that can change, the boundary that must survive, and everything beyond.
            </p>
            <dl className="km-definition-grid">
              <div><dt>U · active</dt><dd>Vertices and edges reachable within radius two.</dd></div>
              <div><dt>L · frontier</dt><dd>Vertices exactly at radius two; the attachment boundary.</dd></div>
              <div><dt>V · external</dt><dd>The frontier, the world beyond it, and all non-U edges.</dd></div>
            </dl>
          </div>
          <figure className="km-state-plate exhibit-rail">
            <GraphRenderer
              state={kuInitial}
              machine={compiledTreeLookupKU}
              layout={layoutTreeLookup(kuInitial)}
              reducedMotion={reducedMotion}
              height={390}
            />
            <figcaption>
              The initial tree-lookup state. Dashed halos mark frontier L; faded vertices belong
              to external part V. The labels are semantic types, not decorative names.
            </figcaption>
          </figure>
        </section>

        <section className="km-section page-rail" aria-labelledby="replacement-heading">
          <div className="section-heading">
            <p className="eyebrow">One step</p>
            <h2 id="replacement-heading">Destroy the interior. Preserve the seam.</h2>
          </div>
          <div className="section-copy">
            <p className="standfirst">
              The rooted typed form of U selects at most one exact pair <code>Uᵢ → Wᵢ</code>.
              Replacement is graph surgery, not a visual metaphor.
            </p>
            <ol className="km-replacement-sequence">
              <li><span>01</span><div><strong>Recognise</strong><p>Canonicalise the rooted typed neighbourhood and find its one matching rule.</p></div></li>
              <li><span>02</span><div><strong>Remove</strong><p>Delete the interior <code>U \ L</code>; the frontier remains in the state.</p></div></li>
              <li><span>03</span><div><strong>Insert</strong><p>Create a fresh copy of Wᵢ and identify its attachment vertices with L.</p></div></li>
              <li><span>04</span><div><strong>Continue or stop</strong><p>A new T0 continues, T1 returns a result, and no matching rule means no result.</p></div></li>
            </ol>
          </div>
        </section>

        <section className="km-lab page-rail" aria-labelledby="lab-heading">
          <div className="km-lab-heading">
            <div>
              <p className="eyebrow">Worked program</p>
              <h2 id="lab-heading">Follow an address through a tree</h2>
            </div>
            <p>
              Choose two left/right instructions. The K machine consumes them by local replacement
              and finishes beside the selected leaf.
            </p>
          </div>

          <div className="km-lab-toolbar">
            <div>
              <span className="km-preset-label">Address</span>
              <div className="km-preset-buttons" aria-label="Tree address presets">
                {TREE_ADDRESS_PRESETS.map((address) => (
                  <button
                    key={address}
                    type="button"
                    className="km-preset-button"
                    aria-pressed={address === treeAddress}
                    onClick={() => setTreeAddress(address)}
                  >
                    {displayAddress(address)}
                  </button>
                ))}
              </div>
            </div>
            <p className="km-selection-result">
              <span>{displayAddress(treeAddress)}</span> leads to leaf <strong>{expectedLeafForAddress(treeAddress)}</strong>
            </p>
            <button
              type="button"
              className="km-comparison-toggle"
              aria-pressed={showComparison}
              onClick={() => setShowComparison((value) => !value)}
            >
              {showComparison ? 'Hide tape comparison' : 'Compare with a tape'}
            </button>
          </div>

          <ComparisonFigure
            key={`${treeAddress}-${showComparison ? 'compare' : 'single'}`}
            kuMachine={compiledTreeLookupKU}
            kuInitial={kuInitial}
            kuLayout={layoutTreeLookup}
            tmMachine={treeLookupTM}
            tmInitial={tmInitial}
            showComparison={showComparison}
            caption={showComparison
              ? 'The same fixed lookup in two native representations. Align by an address symbol consumed, not by raw step count.'
              : 'Step, play, rewind, or inspect the exact replacement pair. Detached address bits remain as valid external garbage.'}
          />
        </section>

        <section className="km-section page-rail" aria-labelledby="limits-heading">
          <div className="section-heading">
            <p className="eyebrow">What this licenses</p>
            <h2 id="limits-heading">A difference in access, not a complexity theorem</h2>
          </div>
          <div className="section-copy">
            <p className="standfirst">
              The K program is a finite radius-two rulebook of 236 exact replacement pairs. The
              tape side is one deliberately narrow program for one serialised depth-two tree.
            </p>
            <p>
              Their counters are native to chosen machines and encodings. The display does not
              report a universal speed-up, a “true” Kolmogorov complexity, or a model-independent
              logical depth.
            </p>
            <details className="km-source-disclosure">
              <summary>Formal constraints and implementation status</summary>
              <div>
                <p>The complex is finite and undirected; every vertex has one ordered type; exactly one vertex has working type T0 or final type T1; and no vertex has two neighbours of the same type.</p>
                <p>This is a modern TypeScript reimplementation from the published definition. The type mnemonics, tree program, layout, animation, and tape serialisation are pedagogical choices. The graph engine contains no layout or animation state.</p>
              </div>
            </details>
            <div className="km-sources">
              <p className="eyebrow">Sources</p>
              <ol>
                <li>A. N. Kolmogorov and V. A. Uspensky, <a href="https://www.mathnet.ru/eng/rm7453">“On the Definition of an Algorithm”</a> (1958; English translation 1963).</li>
                <li>Yuri Gurevich, <a href="https://web.eecs.umich.edu/~gurevich/Opera/78.pdf">“On Kolmogorov Machines and Related Issues”</a> (1988).</li>
                <li>Charles H. Bennett, <a href="https://web.cs.ucdavis.edu/~doty/papers/LogicalDepthAndPhysicalComplexity.pdf">“Logical Depth and Physical Complexity”</a> (1988).</li>
              </ol>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer page-rail">
        <p>Formal object: 1958 KU profile · reimplemented from the published definition</p>
        <a className="site-credit" href="https://www.bengaskin.com/">Ben Gaskin</a>
      </footer>
    </>
  )
}
